import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { restartableTask } from 'ember-concurrency';
import type AccessEventModel from 'garaje/models/access-event';
import type { Field } from 'garaje/models/location';
import type AjaxService from 'garaje/services/ajax';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type StoreService from 'garaje/services/store';
import type WindowLocationService from 'garaje/services/window-location';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import zft from 'garaje/utils/zero-for-tests';
import { alias } from 'macro-decorators';

interface AccessEventsTableAccessEventFeedItemComponentArgs {
  fields: AccessEventsDashboardField[];
  accessEvent: AccessEventModel;
}

export interface AccessEventsDashboardField extends Field {
  sort?: string;
  show?: boolean;
}

/**
 * @param {String}                      context - The context in which the component is being used ("location" or "property"). Passed to EntryApprovalAbility#canReview.
 * @param {Class<AccessEvent>}          accessEvent
 * @param {Array}                       fieldOptions
 * @param {Boolean}                     hasPhoto
 * @param {object}                      location
 */
export default class AccessEventFeedItem extends Component<AccessEventsTableAccessEventFeedItemComponentArgs> {
  @service declare abilities: AbilitiesService;
  @service declare ajax: AjaxService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare windowLocation: WindowLocationService;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  @tracked itemFullNameWasTruncated = false;
  @tracked itemHostWasTruncated = false;

  // Multi-signal corroborating-signals expansion (Presence Heuristics M1).
  @tracked signalsExpanded = false;
  @tracked corroboratingSignals: AccessEventModel[] = [];

  tooltipDelay = zft(400);

  @alias('state.currentCompany') currentCompany!: StateService['currentCompany'];

  // colspan for the denied message is equal the the number of shown fieldOptions minus 1 for the name field, which is already in the row and is not included in the colspan
  get deniedColspan(): number {
    return this.args.fields.filter((field) => field.show).length - 1;
  }

  get isWorkplaceRoute(): boolean {
    return this.router.isActive('workplace');
  }

  get accessEvent(): AccessEventModel {
    return this.args.accessEvent;
  }

  // Only the corroborated sign-in row gets the "View signals" affordance + row expansion.
  get isMultiSignalSignIn(): boolean {
    return this.args.accessEvent.isMultiSignalSignIn;
  }

  // Fetch the signals that corroborated this sign-in — the held signals AND the sign-in itself.
  loadCorroboratingSignals = restartableTask(async () => {
    const { corroboratedEntryId } = this.args.accessEvent;
    if (!corroboratedEntryId) return;
    const results = await this.store.query('access-event', {
      filter: {
        'location-ids': this.state.currentLocation.id,
        'corroborated-entry-ids': corroboratedEntryId,
      },
      page: { limit: 50, offset: 0 },
    });
    this.corroboratingSignals = results.slice();
  });

  // Ordered list for the expansion: the final sign-in first (badge ✓), then the contributing signals
  // newest-first, numbered descending (so the oldest contributing signal is "1"). Drives the timeline.
  get signalTimeline(): Array<{
    signal: AccessEventModel;
    final: boolean;
    step: number | null;
    isFirst: boolean;
    isLast: boolean;
  }> {
    // eventTimestamp is a Date once hydrated through the serializer, but may be a raw ISO string
    // (e.g. when pushed straight into the store), so coerce before comparing.
    const toMillis = (signal: AccessEventModel): number => {
      const ts = signal.eventTimestamp;
      return ts ? new Date(ts).getTime() : 0;
    };
    const signInId = this.args.accessEvent.id;
    const signIn = this.corroboratingSignals.find((s) => s.id === signInId);
    const contributing = this.corroboratingSignals
      .filter((s) => s.id !== signInId)
      .sort((a, b) => toMillis(b) - toMillis(a));

    const ordered: Array<{ signal: AccessEventModel; final: boolean; step: number | null }> = [];
    if (signIn) ordered.push({ signal: signIn, final: true, step: null });
    contributing.forEach((signal, i) => ordered.push({ signal, final: false, step: contributing.length - i }));

    return ordered.map((item, i) => ({ ...item, isFirst: i === 0, isLast: i === ordered.length - 1 }));
  }

  // Toggles the corroborating-signals expansion. Wired to the entire parent row, so it guards on
  // isMultiSignalSignIn — ordinary rows are not expandable and clicking them is a no-op.
  @action
  toggleSignals(): void {
    if (!this.isMultiSignalSignIn) return;
    this.signalsExpanded = !this.signalsExpanded;
    if (this.signalsExpanded && this.corroboratingSignals.length === 0) {
      void this.loadCorroboratingSignals.perform();
    }
  }

  // Keep clicks on interactive children (e.g. the overflow action menu) from bubbling up to the
  // row-level toggle, so opening the menu does not also expand/collapse the signals.
  @action
  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  // The caret button (rendered in the Event Time column) lives inside the clickable row, so it must
  // stop its click from bubbling — otherwise the row handler would toggle a second time.
  @action
  toggleSignalsFromCaret(event: Event): void {
    event.stopPropagation();
    this.toggleSignals();
  }

  @action
  trackOpenActionMenu(access_event_id: string): void {
    this.metrics.trackEvent('Access Event Overflow Opened', { access_event_id });
  }

  @action
  trackClickAccessEventDetail(access_event_id: string): void {
    this.metrics.trackEvent('Access Event Details Clicked', { access_event_id });
  }
}
